/**
 * Phase 132 — GPS is mandatory for every field photo (visit / property photos, survey & reassessment captures).
 *
 * `getStrictPosition()` resolves only with a REAL fix (lat/lng present, in range, not 0/0). It tries a
 * high-accuracy fix first and falls back to a coarse (network) fix — any real fix counts, no accuracy threshold
 * (explicit product decision). When location is off / denied / times out it rejects and the caller shows
 * <GpsRequiredDialog /> with GPS_REQUIRED_MESSAGE and discards the photo. The server enforces the same rule.
 */

export const GPS_REQUIRED_MESSAGE =
  "GPS location is required to upload this photo. Please turn on your device location and try again.";
export const VISIT_PHOTO_REQUIRED_MESSAGE =
  "At least one GPS-tagged photo of the property is required to record this visit. Please capture a GPS photo and try again.";

export function isValidCoords(lat, lng) {
  const la = Number(lat), ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return false;
  if (la < -90 || la > 90 || ln < -180 || ln > 180) return false;
  if (Math.abs(la) < 1e-9 && Math.abs(ln) < 1e-9) return false;   // "null island" = location off
  return true;
}

class GpsError extends Error {
  constructor(code, message) { super(message || code); this.code = code; }
}

const once = (opts) => new Promise((resolve, reject) => {
  navigator.geolocation.getCurrentPosition(resolve, reject, opts);
});

/**
 * Resolve {lat, lng, accuracy} or reject with GpsError(code) where code is one of
 * "unsupported" | "denied" | "unavailable" | "timeout" | "invalid".
 */
export async function getStrictPosition({ highAccuracyTimeout = 12000, fallbackTimeout = 8000, maximumAge = 30000 } = {}) {
  if (typeof navigator === "undefined" || !navigator.geolocation) throw new GpsError("unsupported");
  let pos = null, lastErr = null;
  try {
    pos = await once({ enableHighAccuracy: true, timeout: highAccuracyTimeout, maximumAge });
  } catch (e) {
    lastErr = e;
    if (e && e.code === 1) throw new GpsError("denied");           // PERMISSION_DENIED — no point retrying
    try {
      pos = await once({ enableHighAccuracy: false, timeout: fallbackTimeout, maximumAge });
    } catch (e2) {
      lastErr = e2;
    }
  }
  if (!pos) {
    const code = lastErr && lastErr.code === 1 ? "denied" : lastErr && lastErr.code === 3 ? "timeout" : "unavailable";
    throw new GpsError(code);
  }
  const { latitude, longitude, accuracy } = pos.coords || {};
  if (!isValidCoords(latitude, longitude)) throw new GpsError("invalid");
  return { lat: Number(latitude), lng: Number(longitude), accuracy: Number.isFinite(accuracy) ? Number(accuracy) : null };
}

/** Fire-and-forget: ask for location as soon as the user taps the camera button so the fix is warm
 *  (and the permission prompt appears) before the photo comes back. */
export function warmUpGps() {
  try {
    navigator.geolocation?.getCurrentPosition(() => {}, () => {}, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
  } catch (e) { /* ignore */ }
}

/** YYYY-MM-DD in Indian Standard Time — the business day used for visits and same-day photos. */
export const istDay = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
};

/** GPS-tagged property photos captured TODAY (IST) by this user — they count towards the visit requirement. */
export function gpsPhotosToday(photos, userId) {
  const today = istDay();
  return (photos || []).filter((p) => p && p.file_id && p.uploaded_by === userId && isValidCoords(p.lat, p.lng)
    && istDay(p.captured_at || p.uploaded_at) === today);
}

// ---- GPS registry for photos held on the device as data URLs (survey / reassessment offline captures) ----
const registry = new Map();
export function rememberPhotoGps(dataUrl, gps) {
  if (dataUrl && gps) registry.set(dataUrl, { lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy ?? null, captured_at: gps.captured_at || new Date().toISOString() });
}
export function photoGpsFor(dataUrl) {
  return registry.get(dataUrl) || null;
}
